/**
 * Rol almashtirish logikasi:
 *
 * Mijoz → Kassir: Kassirni tanlaydi → cashierning PIN'ini kiritadi → backend impersonate
 * Kassir → Mijoz: "Mijoz" ni tanlaydi → tenant_ownerning PIN'ini kiritadi → backend switch-to-owner (token almashtirish)
 * Kassir → Kassir: Kassirni tanlaydi → o'sha cashierning PIN'ini kiritadi → backend switch-to-cashier
 *
 * Barcha rol o'zgarishlari backend token almashtirish orqali amalga oshiriladi.
 * PIN o'rnatish LOGIN paytida amalga oshiriladi (LoginModal).
 */

import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useBodyScrollLock } from '../../hooks/useBodyScrollLock';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth, normalizeUser } from '../../auth/authContext';
import type { Role } from '../../config/roles';
import { useCashiers } from '../../hooks/useCashiers';
import {
  impersonateCashier,
  switchToOwner,
  switchToCashier,
  setStoredTokens,
  setStoredSessionId,
  getProfile,
} from '../../services/authApi';
import './RoleSwitchModal.css';

type Step = 'choose' | 'enter_pin';

interface CashierOption {
  id: string;
  name: string;
  phone?: string;
  branchName?: string;
}

interface RoleSwitchModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function RoleSwitchModal({ isOpen, onClose }: RoleSwitchModalProps) {
  useBodyScrollLock(isOpen);
  const { t } = useLanguage();
  const { baseUser, user, setEffectiveRole, setUser } = useAuth();
  const navigate = useNavigate();
  const currentUser = (baseUser ?? user) as { tenantId?: string | number; tenant_id?: string | number } | null;
  const currentTenantId = String(currentUser?.tenantId ?? currentUser?.tenant_id ?? '').trim();
  const { data: cashiersRaw = [] } = useCashiers(currentTenantId ? { tenant_id: currentTenantId } : {});

  const cashiers = useMemo(() => {
    const list = (Array.isArray(cashiersRaw) ? cashiersRaw : []) as CashierOption[];
    const filtered = list.filter((c) => {
      const cTenant = String((c as any)?.tenantId ?? (c as any)?.tenant_id ?? '').trim();
      const active = String((c as any)?.status ?? 'active').toLowerCase() !== 'inactive';
      if (!active) return false;
      if (!currentTenantId) return true;
      return cTenant === '' || cTenant === currentTenantId;
    });
    const seen = new Set<string>();
    return filtered.filter((c) => {
      const id = String(c.id || '').trim();
      if (!id || seen.has(id)) return false;
      seen.add(id);
      return true;
    });
  }, [cashiersRaw, currentTenantId]);

  const [step, setStep] = useState<Step>('choose');
  const [pin, setPin] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  // Maqsad: targetMijoz=true → tenant_owner roliga; targetCashierId → o'sha cashier roliga
  const [targetMijoz, setTargetMijoz] = useState(false);
  const [targetCashierId, setTargetCashierId] = useState<string | null>(null);

  const realRole = (baseUser ?? user)?.role as Role | undefined;
  const effectiveRole = user?.role as Role | undefined;
  const canUse = realRole === 'tenant_owner' || realRole === 'cashier';
  const isMijoz = effectiveRole === 'tenant_owner';

  useEffect(() => {
    if (!isOpen) {
      setStep('choose');
      setPin('');
      setError('');
      setLoading(false);
      setTargetMijoz(false);
      setTargetCashierId(null);
    }
  }, [isOpen]);

  // Mijoz cashier tanladi → o'sha cashierning PIN'ini so'raymiz
  const handleMijozChooseKassir = useCallback((cashierId: string) => {
    setTargetMijoz(false);
    setTargetCashierId(cashierId);
    setStep('enter_pin');
    setPin('');
    setError('');
  }, []);

  // Kassir maqsad tanladi (Mijoz yoki boshqa Kassir)
  const handleKassirChooseTarget = useCallback(
    (toMijoz: boolean, cashierId: string | null) => {
      // PIN holatini frontendda bloklamaymiz: token switchdan keyin user state kechikib yangilanadi.
      // Backend PIN'ni tekshiradi; bu yerda faqat PIN kiritish oynasiga o'tkazamiz.
      setTargetMijoz(toMijoz);
      setTargetCashierId(toMijoz ? null : cashierId);
      setStep('enter_pin');
      setPin('');
      setError('');
    },
    [baseUser, user, t]
  );

  /**
   * PIN kiritilgandan keyin backend orqali real token almashtirish.
   * Maqsad rolning PIN'ini tekshiradi — o'z PIN'ini emas.
   *
   * isMijoz && targetCashierId → impersonateCashier(cashierId, pin)  [cashierning PIN'i]
   * !isMijoz && targetMijoz   → switchToOwner(pin)                  [tenant_ownerning PIN'i]
   * !isMijoz && targetCashierId → switchToCashier(cashierId, pin)    [o'sha cashierning PIN'i]
   */
  const handleEnterPin = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      const p = pin.trim();
      if (!p) {
        setError(t('roleSwitch_pin_required') || 'PIN kiriting');
        return;
      }
      if (p.length < 4) {
        setError(t('roleSwitch_pin_min') || 'PIN kamida 4 ta raqam bo\'lishi kerak');
        return;
      }
      setError('');
      setLoading(true);

      (async () => {
        try {
          let res: Record<string, unknown>;

          if (isMijoz && targetCashierId) {
            // Mijoz → Kassir: cashierning PIN'i
            res = await impersonateCashier(targetCashierId, p) as Record<string, unknown>;
          } else if (!isMijoz && targetMijoz) {
            // Kassir → Mijoz: tenant_ownerning PIN'i (backend JWT'dan egani topadi)
            res = await switchToOwner(p) as Record<string, unknown>;
          } else if (!isMijoz && targetCashierId) {
            // Kassir → boshqa Kassir: o'sha cashierning PIN'i
            res = await switchToCashier(targetCashierId, p) as Record<string, unknown>;
          } else {
            setError('Noto\'g\'ri holat');
            setLoading(false);
            return;
          }

          const accessToken =
            (res?.access_token ?? res?.accessToken ?? res?.token) as string | undefined;
          const refreshToken =
            (res?.refresh_token ?? res?.refreshToken) as string | undefined;
          const sessionId =
            (res?.session_id ?? res?.sessionId) as string | undefined;
          const nextUserRaw = (res?.user ?? res?.cashier ?? null) as Record<string, unknown> | null;

          if (!accessToken) {
            // Backend PIN xato deb qaytargan bo'lishi mumkin
            const msg =
              (res?.message as string) ??
              (t('roleSwitch_pin_invalid') || 'PIN noto\'g\'ri yoki server xatosi');
            setError(msg);
            setLoading(false);
            return;
          }

          setStoredTokens({
            accessToken: String(accessToken),
            refreshToken: refreshToken ? String(refreshToken) : null,
          });
          setStoredSessionId(sessionId ? String(sessionId) : null);

          // Backend user qaytarmasa ham, token almashtirilgandan keyin profilni qayta yuklaymiz.
          const resolvedUserRaw = nextUserRaw ?? (await getProfile().catch(() => null));
          if (resolvedUserRaw) {
            const normalized = normalizeUser(resolvedUserRaw as Parameters<typeof normalizeUser>[0]);
            setUser(normalized);
          }
          setEffectiveRole(null, null);

          setPin('');
          onClose();
          navigate('/', { replace: true });
        } catch (err: unknown) {
          const e = err as { response?: { data?: { message?: string } }; message?: string };
          const msg =
            e?.response?.data?.message ??
            e?.message ??
            (t('roleSwitch_pin_invalid') || 'PIN noto\'g\'ri');
          setError(msg);
        } finally {
          setLoading(false);
        }
      })();
    },
    [
      pin, isMijoz, targetMijoz, targetCashierId,
      setEffectiveRole, setUser, navigate, onClose, t,
    ]
  );

  const handleClose = useCallback(() => {
    setStep('choose');
    setPin('');
    setError('');
    onClose();
  }, [onClose]);

  if (!isOpen || !canUse) return null;

  // PIN kiritish formida qaysi rolning PIN'i kerakligini ko'rsatamiz
  const pinHint = isMijoz
    ? t('roleSwitch_enter_cashier_pin') || 'Kassirning PIN\'ini kiriting'
    : targetMijoz
    ? t('roleSwitch_enter_tenant_owner_pin') || 'Mijoz (egasining) PIN\'ini kiriting'
    : t('roleSwitch_enter_cashier_pin') || 'Kassirning PIN\'ini kiriting';

  return (
    <div
      className="role-switch-overlay"
      onClick={handleClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="role-switch-title"
    >
      <div className="role-switch-modal" onClick={(e) => e.stopPropagation()}>
        <button
          type="button"
          className="role-switch-close"
          onClick={handleClose}
          aria-label={t('loginModalClose') || 'Yopish'}
        />
        <h2 id="role-switch-title" className="role-switch-title">
          {t('roleSwitch_title') || 'Ro\'l almashtirish'}
        </h2>

        {step === 'choose' && (
          <>
            <p className="role-switch-desc">
              {t('roleSwitch_current') || 'Hozirgi ro\'l'}:{' '}
              <strong>{user?.role ? t(`roleTitle_${user.role}`) : ''}</strong>
            </p>
            {error && <p className="role-switch-error" role="alert">{error}</p>}
            {isMijoz ? (
              <div className="role-switch-list-wrap">
                <p className="role-switch-list-caption">
                  {t('roleSwitch_choose_cashier') || 'Kassir ro\'liga o\'tish (cashierning PIN\'i kerak)'}
                </p>
                <ul className="role-switch-list">
                  {cashiers.length === 0 ? (
                    <li className="role-switch-list-empty">
                      {t('roleSwitch_no_cashiers') || 'Kassirlar topilmadi'}
                    </li>
                  ) : (
                    cashiers.map((c) => (
                      <li key={c.id}>
                        <button
                          type="button"
                          className="role-switch-list-btn"
                          onClick={() => handleMijozChooseKassir(c.id)}
                        >
                          <span className="role-switch-list-name">{c.name || c.id}</span>
                          {c.phone && (
                            <span className="role-switch-list-meta">{c.phone}</span>
                          )}
                        </button>
                      </li>
                    ))
                  )}
                </ul>
              </div>
            ) : (
              <div className="role-switch-list-wrap">
                <p className="role-switch-list-caption">
                  {t('roleSwitch_choose_target') || 'Qaysi ro\'lga o\'tmoqchisiz?'}
                </p>
                <ul className="role-switch-list">
                  <li>
                    <button
                      type="button"
                      className="role-switch-list-btn"
                      onClick={() => handleKassirChooseTarget(true, null)}
                    >
                      <span className="role-switch-list-name">{t('roleTitle_tenant_owner')}</span>
                      <span className="role-switch-list-meta">
                        {t('roleSwitch_pin_required') || 'Mijoz PIN\'i kerak'}
                      </span>
                    </button>
                  </li>
                  {cashiers
                    .filter((c) => c.id !== (baseUser ?? user)?.id)
                    .map((c) => (
                      <li key={c.id}>
                        <button
                          type="button"
                          className="role-switch-list-btn"
                          onClick={() => handleKassirChooseTarget(false, c.id)}
                        >
                          <span className="role-switch-list-name">{c.name || c.id}</span>
                          {c.phone && (
                            <span className="role-switch-list-meta">{c.phone}</span>
                          )}
                        </button>
                      </li>
                    ))}
                </ul>
              </div>
            )}
          </>
        )}

        {step === 'enter_pin' && (
          <form onSubmit={handleEnterPin} className="role-switch-form">
            <p className="role-switch-desc">{pinHint}</p>
            <label htmlFor="role-switch-pin" className="role-switch-label">
              {t('roleSwitch_pin_label') || 'PIN'}
            </label>
            <input
              id="role-switch-pin"
              type="password"
              inputMode="numeric"
              autoComplete="off"
              className="role-switch-input"
              value={pin}
              onChange={(e) => {
                setPin(e.target.value.replace(/\D/g, '').slice(0, 8));
                setError('');
              }}
              placeholder="••••"
              maxLength={8}
              autoFocus
              disabled={loading}
            />
            {error && (
              <p className="role-switch-error" role="alert">
                {error}
              </p>
            )}
            <div className="role-switch-actions">
              <button
                type="button"
                className="role-switch-btn role-switch-btn-secondary"
                onClick={() => {
                  setStep('choose');
                  setError('');
                }}
                disabled={loading}
              >
                {t('adminBranch_cancel') || 'Bekor qilish'}
              </button>
              <button
                type="submit"
                className="role-switch-btn role-switch-btn-primary"
                disabled={loading}
              >
                {loading
                  ? (t('loginModalChecking') || 'Tekshirilmoqda...')
                  : (t('roleSwitch_confirm') || 'Ro\'lni almashtirish')}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
